import React from 'react';
import { Building2, Link2, Search, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useRegisterTopEscapeClose } from '../hooks/useEscapeCloseStack';
import {
  DEAL_PURPOSE_ACQUISITION,
  DEAL_PURPOSE_OWNED,
  FINANCING_TYPE_EXISTING,
} from '../utils/dealPurpose';

/**
 * Modal shown when creating a new deal: acquisition analysis vs owned property.
 */
export default function DealPurposeModal({ open, onClose, onSelect }) {
  const { t } = useLanguage();
  useRegisterTopEscapeClose(open ? onClose : null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="deal-purpose-modal-title"
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        data-onboarding="deal-purpose-modal"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-2">
          <div>
            <h2 id="deal-purpose-modal-title" className="text-xl font-semibold text-gray-900">
              {t('dealPurpose.modal.title')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">{t('dealPurpose.modal.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-4 sm:px-6 pb-6 pt-3 grid gap-3 grid-cols-1">
          <button
            type="button"
            onClick={() =>
              onSelect({
                dealPurpose: DEAL_PURPOSE_ACQUISITION,
                financingType: 'conventional',
              })
            }
            className="text-left border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Search className="w-8 h-8 text-blue-600 mb-3" />
            <div className="font-semibold text-gray-900">{t('dealPurpose.acquisition.title')}</div>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {t('dealPurpose.acquisition.description')}
            </p>
          </button>
          <button
            type="button"
            onClick={() =>
              onSelect({
                dealPurpose: DEAL_PURPOSE_OWNED,
                financingType: FINANCING_TYPE_EXISTING,
              })
            }
            className="text-left border border-gray-200 rounded-xl p-4 hover:border-teal-400 hover:bg-teal-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <Building2 className="w-8 h-8 text-teal-600 mb-3" />
            <div className="font-semibold text-gray-900">{t('dealPurpose.owned.title')}</div>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {t('dealPurpose.owned.description')}
            </p>
          </button>
          <button
            type="button"
            onClick={() =>
              onSelect({
                dealPurpose: DEAL_PURPOSE_ACQUISITION,
                financingType: 'conventional',
                importListing: true,
              })
            }
            className="text-left border border-gray-200 rounded-xl p-4 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-onboarding="deal-purpose-import-listing"
          >
            <Link2 className="w-8 h-8 text-indigo-600 mb-3" />
            <div className="font-semibold text-gray-900">{t('dealPurpose.importListing.title')}</div>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {t('dealPurpose.importListing.description')}
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
